/**
 * BankSelector — Mobile Banking bank picker
 *
 * Shows available banks (BBL, TTB Touch) from ReservePay's supported list.
 * On select: calls selectPaymentMethod(bank_code) → onSubmit(paymentSessionId).
 */
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2, Building2, ArrowLeft } from 'lucide-react'
import { MOBILE_BANKING_OPTIONS, type MobileBankCode } from '@/lib/payment-flags'

interface BankSelectorProps {
  amount: number
  onSubmit: (paymentSessionId: string, bankCode: MobileBankCode) => void
  onCancel: () => void
}

export function BankSelector({ amount, onSubmit, onCancel }: BankSelectorProps) {
  const [loading, setLoading] = useState<MobileBankCode | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Reuse selectCardPayment as a generic "select with payment_method" — 
  // for mobile banking we use the same backend endpoint but with different payment_method.
  async function handleSelectBank(bankCode: MobileBankCode) {
    setLoading(bankCode)
    setError(null)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authentication required')

      const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
      const MERCHANT_ID = import.meta.env.VITE_RESERVEPAY_MERCHANT_ID || ''
      const INSTALLATION_ID = import.meta.env.VITE_RESERVEPAY_INSTALLATION_ID || ''
      const amountSatang = String(Math.round(amount * 100))

      // Select payment method with bank code
      const res = await fetch(`${API_BASE}/api/payment/user/reservepay/select-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          merchant_id: MERCHANT_ID,
          installation_id: INSTALLATION_ID,
          amount: amountSatang,
          currency: 'THB',
          payment_method: bankCode,
        }),
      })

      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Failed to select ${bankCode}`)
      }

      const paymentSessionId = data?.data?.payment_session_id
      if (!paymentSessionId) throw new Error('No payment session ID returned')
      onSubmit(paymentSessionId, bankCode)
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center space-y-1">
        <p className="text-sm text-gray-500">จำนวนเงิน</p>
        <p className="text-3xl font-bold text-[#7FB069]">
          ฿{amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </p>
        <p className="text-sm text-gray-400">เลือกธนาคารเพื่อชำระผ่าน Mobile Banking</p>
      </div>

      <div className="space-y-3">
        {MOBILE_BANKING_OPTIONS.map((bank) => (
          <button
            key={bank.name}
            type="button"
            disabled={loading !== null}
            onClick={() => handleSelectBank(bank.name)}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all w-full text-left ${
              loading === bank.name
                ? 'border-purple-400 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'
            }`}
          >
            <span className="text-2xl">{bank.logo}</span>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">{bank.displayName}</p>
              <p className="text-xs text-gray-400">ชำระผ่านแอป Mobile Banking</p>
            </div>
            {loading === bank.name ? (
              <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
            ) : (
              <Building2 className="w-5 h-5 text-gray-300" />
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
          {error}
        </div>
      )}

      <Button variant="outline" onClick={onCancel} disabled={loading !== null}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        ย้อนกลับ
      </Button>
    </div>
  )
}
